import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase, isSupabaseEnabled } from '../lib/supabaseClient';
import { IdentityLevel } from '../lib/identity';

// Arena enrolment surface — implements
// docs/audit/2026-05-14-arena-enrolment-brief.md §4.
//
// Two-step flow:
//   Step 1 — collect email, fire supabase.auth.signInWithOtp. User
//            clicks magic-link in their inbox and returns to the
//            cockpit. The redirect URL is window.location.href so
//            they land back exactly where they left.
//   Step 2 — collect username + age + T&Cs checkboxes. Insert the
//            enrolled profile row (id = auth user UUID as TEXT,
//            auth_user_id linked), call set_username RPC for
//            validation + assignment, then flip enrolment_state to
//            'enrolled' and stamp the acceptance timestamps.
//
// The cockpit decides which step to mount with — Step 1 for a fresh
// ENROL click, Step 2 when an authenticated session returns from the
// magic-link click with enrolment still incomplete.

const TNC_VERSION = '2026-05-19-v2';

type Step = 1 | 2;

type Availability =
  | { state: 'idle' }
  | { state: 'checking' }
  | { state: 'invalid'; reason: 'LENGTH_OUT_OF_RANGE' | 'INVALID_FORMAT' | 'EMPTY' }
  | { state: 'reserved' }
  | { state: 'taken' }
  | { state: 'available' };

interface Props {
  onClose:     () => void;
  onEnrolled:  (enrolledProfileId: string, email: string, username: string) => void;
  initialStep: Step;
  // Step 2 entry: when the cockpit detects the post-magic-link auth
  // session, it hands the resolved auth user id + email through so
  // the modal doesn't have to re-derive them.
  authUserId?: string;
  authEmail?:  string;
}

// ── Username validation (client-side mirror of set_username RPC) ────
// Best-effort preview before the RPC runs. The RPC is the
// authoritative validator; this is purely UX feedback.

function classifyFormat(raw: string): Availability['state'] | null {
  const v = raw.trim();
  if (v === '') return 'invalid';
  if (v.length < 3 || v.length > 24) return 'invalid';
  if (!/^[A-Za-z0-9][A-Za-z0-9_-]*[A-Za-z0-9]$/.test(v)) return 'invalid';
  if (/(--|__|_-|-_)/.test(v)) return 'invalid';
  return null;
}

// ── Step 1 — email + magic-link ─────────────────────────────────────

function Step1({
  email,
  setEmail,
  busy,
  error,
  onSend,
  awaitingLink,
  otpCode,
  setOtpCode,
  otpBusy,
  otpError,
  onVerifyOtp,
}: {
  email:         string;
  setEmail:      (s: string) => void;
  busy:          boolean;
  error:         string | null;
  onSend:        () => void;
  awaitingLink:  boolean;
  otpCode:       string;
  setOtpCode:    (s: string) => void;
  otpBusy:       boolean;
  otpError:      string | null;
  onVerifyOtp:   () => void;
}): React.JSX.Element {
  const emailValid = /.+@.+\..+/.test(email.trim());

  if (awaitingLink) {
    const codeValid = /^\d{6}$/.test(otpCode.trim());
    return (
      <>
        <p style={{
          color: '#c7d2fe', fontSize: 12, lineHeight: 1.6, margin: '0 0 10px',
        }}>
          Check your inbox at <strong style={{ color: '#fff' }}>{email}</strong>.
          Click the sign-in link <em>or</em> enter the 6-digit code from the
          email below.
        </p>
        <p style={{
          color: '#64748b', fontSize: 10, letterSpacing: '0.08em', margin: '0 0 16px',
        }}>
          Use the code when you&rsquo;re reading email on a different device
          (phone) than the one you&rsquo;re signing in on (desktop). Links
          arrive from <code style={{ color: '#a5b4fc' }}>arena@agoracle.io</code>.
          Check spam if not arrived in 30 seconds.
        </p>

        <label
          htmlFor="enrolment-otp"
          style={{ display: 'block', color: '#94a3b8', fontSize: 10, letterSpacing: '0.16em', marginBottom: 6 }}
        >
          6-DIGIT CODE
        </label>
        <input
          id="enrolment-otp"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          pattern="[0-9]{6}"
          maxLength={6}
          value={otpCode}
          onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          disabled={otpBusy}
          style={{
            width:         '100%',
            padding:       '8px 10px',
            fontFamily:    'monospace',
            fontSize:      18,
            letterSpacing: '0.36em',
            textAlign:     'center',
            color:         '#e0e7ff',
            background:    '#020617',
            border:        '1px solid #334155',
            borderRadius:  4,
            outline:       'none',
          }}
        />

        {otpError && (
          <p style={{ color: '#fca5a5', fontSize: 10, marginTop: 10, letterSpacing: '0.05em' }}>
            {otpError}
          </p>
        )}

        <button
          onClick={onVerifyOtp}
          disabled={!codeValid || otpBusy}
          style={{
            marginTop:     14,
            width:         '100%',
            padding:       '9px 14px',
            fontFamily:    'monospace',
            fontSize:      11,
            letterSpacing: '0.18em',
            color:         codeValid && !otpBusy ? '#e0e7ff' : '#475569',
            background:    codeValid && !otpBusy ? '#4338ca' : '#1e1b4b',
            border:        `1px solid ${codeValid && !otpBusy ? '#6366f1' : '#334155'}`,
            borderRadius:  4,
            cursor:        codeValid && !otpBusy ? 'pointer' : 'default',
            textTransform: 'uppercase',
          }}
        >
          {otpBusy ? 'Verifying…' : 'Verify code'}
        </button>
      </>
    );
  }

  return (
    <>
      <p style={{
        color: '#c7d2fe', fontSize: 12, lineHeight: 1.6, margin: '0 0 14px',
      }}>
        Enter your email — we&rsquo;ll send you a sign-in link. No password
        needed. New visitors will be asked to choose a username; returning
        visitors are signed straight in.
      </p>

      <label
        htmlFor="enrolment-email"
        style={{ display: 'block', color: '#94a3b8', fontSize: 10, letterSpacing: '0.16em', marginBottom: 6 }}
      >
        EMAIL
      </label>
      <input
        id="enrolment-email"
        type="email"
        autoComplete="email"
        autoFocus
        value={email}
        onChange={e => setEmail(e.target.value)}
        placeholder="you@example.com"
        disabled={busy}
        style={{
          width:        '100%',
          padding:      '8px 10px',
          fontFamily:   'monospace',
          fontSize:     12,
          color:        '#e0e7ff',
          background:   '#020617',
          border:       '1px solid #334155',
          borderRadius: 4,
          outline:      'none',
        }}
      />

      {error && (
        <p style={{ color: '#fca5a5', fontSize: 10, marginTop: 10, letterSpacing: '0.05em' }}>
          {error}
        </p>
      )}

      <button
        onClick={onSend}
        disabled={!emailValid || busy}
        style={{
          marginTop:     16,
          width:         '100%',
          padding:       '9px 14px',
          fontFamily:    'monospace',
          fontSize:      11,
          letterSpacing: '0.18em',
          color:         emailValid && !busy ? '#e0e7ff' : '#475569',
          background:    emailValid && !busy ? '#4338ca' : '#1e1b4b',
          border:        `1px solid ${emailValid && !busy ? '#6366f1' : '#334155'}`,
          borderRadius:  4,
          cursor:        emailValid && !busy ? 'pointer' : 'default',
          textTransform: 'uppercase',
        }}
      >
        {busy ? 'Sending…' : 'Send me a link'}
      </button>
    </>
  );
}

// ── Step 2 — username + age + T&Cs ──────────────────────────────────

function Step2({
  username,         setUsername,
  ageChecked,       setAgeChecked,
  tncChecked,       setTncChecked,
  marketingOptIn,   setMarketingOptIn,
  availability,
  busy,
  error,
  onComplete,
}: {
  username:          string;
  setUsername:       (s: string) => void;
  ageChecked:        boolean;
  setAgeChecked:     (b: boolean) => void;
  tncChecked:        boolean;
  setTncChecked:     (b: boolean) => void;
  marketingOptIn:    boolean;
  setMarketingOptIn: (b: boolean) => void;
  availability:      Availability;
  busy:              boolean;
  error:             string | null;
  onComplete:        () => void;
}): React.JSX.Element {
  const canSubmit =
    !busy &&
    ageChecked &&
    tncChecked &&
    availability.state === 'available';

  const availabilityMsg = (() => {
    switch (availability.state) {
      case 'idle':      return null;
      case 'checking':  return { color: '#64748b', text: 'Checking…' };
      case 'invalid':   return { color: '#fbbf24', text: '3–24 chars · letters, numbers, _ or - · no edges or doubles' };
      case 'reserved':  return { color: '#fca5a5', text: 'That username is reserved.' };
      case 'taken':     return { color: '#fca5a5', text: 'That username is taken.' };
      case 'available': return { color: '#86efac', text: 'Available.' };
    }
  })();

  return (
    <>
      <p style={{
        color: '#c7d2fe', fontSize: 12, lineHeight: 1.6, margin: '0 0 14px',
      }}>
        Signed in. Choose how the Arena will know you.
      </p>

      <label
        htmlFor="enrolment-username"
        style={{ display: 'block', color: '#94a3b8', fontSize: 10, letterSpacing: '0.16em', marginBottom: 6 }}
      >
        USERNAME
      </label>
      <input
        id="enrolment-username"
        type="text"
        autoComplete="username"
        autoFocus
        value={username}
        onChange={e => setUsername(e.target.value)}
        placeholder="3–24 chars, letters/numbers/_/-"
        disabled={busy}
        style={{
          width:        '100%',
          padding:      '8px 10px',
          fontFamily:   'monospace',
          fontSize:     12,
          color:        '#e0e7ff',
          background:   '#020617',
          border:       '1px solid #334155',
          borderRadius: 4,
          outline:      'none',
        }}
      />
      {availabilityMsg && (
        <p style={{
          color:         availabilityMsg.color,
          fontSize:      10,
          margin:        '6px 0 0',
          letterSpacing: '0.05em',
          minHeight:     12,
        }}>
          {availabilityMsg.text}
        </p>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 16 }}>
        <input
          id="enrolment-age"
          type="checkbox"
          checked={ageChecked}
          disabled={busy}
          onChange={e => setAgeChecked(e.target.checked)}
          style={{ marginTop: 2 }}
        />
        <label
          htmlFor="enrolment-age"
          style={{ color: '#c7d2fe', fontSize: 11, lineHeight: 1.5, cursor: 'pointer' }}
        >
          I confirm I am 18 or over.
        </label>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10 }}>
        <input
          id="enrolment-tnc"
          type="checkbox"
          checked={tncChecked}
          disabled={busy}
          onChange={e => setTncChecked(e.target.checked)}
          style={{ marginTop: 2 }}
        />
        <label
          htmlFor="enrolment-tnc"
          style={{ color: '#c7d2fe', fontSize: 11, lineHeight: 1.5, cursor: 'pointer' }}
        >
          I accept the{' '}
          <a
            href="/terms.html"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#a5b4fc', textDecoration: 'underline' }}
          >
            Arena Terms &amp; Conditions
          </a>
          {' '}(version {TNC_VERSION}).
        </label>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10 }}>
        <input
          id="enrolment-marketing-opt-in"
          type="checkbox"
          checked={marketingOptIn}
          disabled={busy}
          onChange={e => setMarketingOptIn(e.target.checked)}
          style={{ marginTop: 2 }}
        />
        <label
          htmlFor="enrolment-marketing-opt-in"
          style={{ color: '#94a3b8', fontSize: 11, lineHeight: 1.5, cursor: 'pointer' }}
        >
          Send me occasional updates about new markets and platform news.{' '}
          <span style={{ color: '#64748b' }}>(Optional.)</span>
        </label>
      </div>

      {error && (
        <p style={{ color: '#fca5a5', fontSize: 10, marginTop: 12, letterSpacing: '0.05em' }}>
          {error}
        </p>
      )}

      <button
        onClick={onComplete}
        disabled={!canSubmit}
        style={{
          marginTop:     18,
          width:         '100%',
          padding:       '9px 14px',
          fontFamily:    'monospace',
          fontSize:      11,
          letterSpacing: '0.18em',
          color:         canSubmit ? '#e0e7ff' : '#475569',
          background:    canSubmit ? '#4338ca' : '#1e1b4b',
          border:        `1px solid ${canSubmit ? '#6366f1' : '#334155'}`,
          borderRadius:  4,
          cursor:        canSubmit ? 'pointer' : 'default',
          textTransform: 'uppercase',
        }}
      >
        {busy ? 'Enrolling…' : 'Complete enrolment'}
      </button>
    </>
  );
}

// ── Modal shell ─────────────────────────────────────────────────────

export default function EnrolmentModal({
  onClose, onEnrolled, initialStep, authUserId, authEmail,
}: Props): React.JSX.Element {
  const [step,         setStep]         = useState<Step>(initialStep);
  const [email,        setEmail]        = useState<string>(authEmail ?? '');
  const [username,     setUsername]     = useState<string>('');
  const [ageChecked,   setAgeChecked]   = useState<boolean>(false);
  const [tncChecked,   setTncChecked]   = useState<boolean>(false);
  // T&Cs v2 (2026-05-19) — marketing opt-in defaults OFF. The
  // transactional opt-in stays implicit at enrolment (column default
  // TRUE on insert) — opting out at enrolment would prevent the
  // platform from sending the sign-in OTP, which is the only way back
  // in. The forthcoming preferences surface exposes both toggles.
  const [marketingOptIn, setMarketingOptIn] = useState<boolean>(false);
  const [availability, setAvailability] = useState<Availability>({ state: 'idle' });
  const [busy,         setBusy]         = useState<boolean>(false);
  const [error,        setError]        = useState<string | null>(null);
  const [awaitingLink, setAwaitingLink] = useState<boolean>(false);
  // Borrowed-desktop OTP path (2026-05-16): magic-link clicks on a
  // phone authenticate the phone, not the desktop the visitor typed
  // their email into. Supabase issues the same token as both a
  // click-through link and a 6-digit code; the code-entry surface
  // here lets the visitor bridge devices.
  const [otpCode,      setOtpCode]      = useState<string>('');
  const [otpBusy,      setOtpBusy]      = useState<boolean>(false);
  const [otpError,     setOtpError]     = useState<string | null>(null);

  // Sync step if the parent flips us mid-life (Step 1 → Step 2 when
  // the magic-link click returns).
  useEffect(() => { setStep(initialStep); }, [initialStep]);
  useEffect(() => { if (authEmail && !email) setEmail(authEmail); }, [authEmail, email, setEmail]);

  // ── Username availability check (debounced, client-side preview) ─
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (step !== 2) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = username.trim();
    if (trimmed === '') { setAvailability({ state: 'idle' }); return; }

    const formatProblem = classifyFormat(trimmed);
    if (formatProblem === 'invalid') {
      setAvailability({ state: 'invalid', reason: 'INVALID_FORMAT' });
      return;
    }

    setAvailability({ state: 'checking' });
    debounceRef.current = setTimeout(() => {
      void (async () => {
        if (!supabase) { setAvailability({ state: 'available' }); return; }
        const lower = trimmed.toLowerCase();

        // Reserved check — RLS allows SELECT to anon/authenticated.
        const { data: reservedRows, error: reservedErr } = await supabase
          .from('reserved_usernames')
          .select('username')
          .ilike('username', lower)
          .limit(1);
        if (reservedErr) {
          console.error('[EnrolmentModal] reserved check failed', reservedErr);
          setAvailability({ state: 'idle' });
          return;
        }
        if (reservedRows && reservedRows.length > 0) {
          setAvailability({ state: 'reserved' });
          return;
        }

        // Taken check — enrolled rows only (matches the partial unique
        // index). The RPC re-checks server-side; this is preview.
        const { data: takenRows, error: takenErr } = await supabase
          .from('profiles')
          .select('id')
          .ilike('username', trimmed)
          .eq('enrolment_state', 'enrolled')
          .limit(1);
        if (takenErr) {
          console.error('[EnrolmentModal] taken check failed', takenErr);
          setAvailability({ state: 'idle' });
          return;
        }
        if (takenRows && takenRows.length > 0) {
          setAvailability({ state: 'taken' });
          return;
        }

        setAvailability({ state: 'available' });
      })();
    }, 300);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [username, step]);

  // ── Step 1 action ────────────────────────────────────────────────
  const sendMagicLink = useCallback(async () => {
    setError(null);
    if (!isSupabaseEnabled() || !supabase) {
      setError('Sign-in is offline right now. Please try again later.');
      return;
    }
    const trimmed = email.trim();
    setBusy(true);
    try {
      const { error: otpErr } = await supabase.auth.signInWithOtp({
        email: trimmed,
        options: { emailRedirectTo: window.location.href },
      });
      if (otpErr) {
        console.error('[EnrolmentModal] OTP send failed', otpErr);
        setError("Couldn't send the magic-link right now. Try again in a moment.");
        return;
      }
      setAwaitingLink(true);
      // Reset the code-entry surface in case the user is re-requesting
      // after an earlier failed attempt or expired token.
      setOtpCode('');
      setOtpError(null);
    } catch (e) {
      console.error('[EnrolmentModal] OTP send threw', e);
      setError("Couldn't send the magic-link right now. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  }, [email]);

  // ── OTP code verification ────────────────────────────────────────
  // Same Supabase token as the magic-link click-through; verifying
  // the 6-digit code establishes the auth session in this browser
  // tab and fires onAuthStateChange. GhostIdentityProvider's
  // resolveSession picks it up and flips identity.enrolment.status
  // — MultiPage's existing effects then either auto-open Step 2
  // (new enrolment) or auto-close the modal (returning visitor).
  const verifyOtpCode = useCallback(async () => {
    setOtpError(null);
    if (!isSupabaseEnabled() || !supabase) {
      setOtpError('Sign-in is offline right now. Please try again later.');
      return;
    }
    const trimmedEmail = email.trim();
    const trimmedCode  = otpCode.trim();
    if (!/^\d{6}$/.test(trimmedCode)) {
      setOtpError('Enter the 6-digit code from your email.');
      return;
    }
    setOtpBusy(true);
    try {
      const { error: verifyErr } = await supabase.auth.verifyOtp({
        email: trimmedEmail,
        token: trimmedCode,
        type:  'email',
      });
      if (verifyErr) {
        console.error('[EnrolmentModal] verifyOtp failed', verifyErr);
        const msg = (verifyErr.message ?? '').toLowerCase();
        if (msg.includes('expired')) {
          setOtpError('That code has expired. Request a new one.');
        } else if (msg.includes('invalid') || msg.includes('not found') || msg.includes('token')) {
          setOtpError("Code didn't match. Double-check the 6 digits from your email.");
        } else {
          setOtpError("Couldn't verify the code. Try again.");
        }
        return;
      }
      // Success path: Supabase has set the session in storage and
      // fired onAuthStateChange. The identity provider's listener
      // re-runs resolveSession, identity.enrolment.status flips,
      // and the cockpit's modal-lifecycle effects do the rest.
    } catch (e) {
      console.error('[EnrolmentModal] verifyOtp threw', e);
      setOtpError("Couldn't verify the code. Try again.");
    } finally {
      setOtpBusy(false);
    }
  }, [email, otpCode]);

  // ── Step 2 action ────────────────────────────────────────────────
  const completeEnrolment = useCallback(async () => {
    setError(null);
    if (!isSupabaseEnabled() || !supabase) {
      setError('Enrolment is offline right now. Please try again later.');
      return;
    }
    if (!authUserId) {
      console.error('[EnrolmentModal] completeEnrolment called without authUserId');
      setError("Couldn't confirm your sign-in. Please reload and click the magic-link again.");
      return;
    }
    const trimmedName  = username.trim();
    const trimmedEmail = (authEmail ?? email).trim();

    setBusy(true);
    try {
      // Step 2a — ensure a profile row exists keyed on the auth user.
      // ON CONFLICT (id) DO NOTHING: idempotent re-run if the user
      // bounced through Step 2 twice.
      const enrolledProfileId = authUserId; // profile.id is TEXT; use the auth UUID as a string

      const { error: insertErr } = await supabase
        .from('profiles')
        .upsert(
          {
            id:               enrolledProfileId,
            auth_user_id:     authUserId,
            email:            trimmedEmail,
            enrolment_state:  'ghost',  // RPC will flip to 'enrolled' after success
          },
          { onConflict: 'id', ignoreDuplicates: true },
        );
      if (insertErr) {
        console.error('[EnrolmentModal] profile upsert failed', insertErr);
        setError("Couldn't create your enrolled profile. Try again in a moment.");
        return;
      }

      // Step 2b — set_username RPC (validates + assigns).
      const { data: rpcData, error: rpcErr } = await supabase.rpc(
        'set_username',
        { p_username: trimmedName },
      );
      if (rpcErr) {
        console.error('[EnrolmentModal] set_username RPC failed', rpcErr);
        setError("Couldn't set username — try again.");
        return;
      }

      type Ok       = { ok: true;  username: string };
      type Refused  = { ok: false; reason: string };
      const result = rpcData as Ok | Refused;

      if (!result.ok) {
        switch (result.reason) {
          case 'TAKEN':                  setError('That username is taken.'); break;
          case 'RESERVED':               setError('That username is reserved. Try another.'); break;
          case 'INVALID_FORMAT':         setError('Invalid format. 3–24 chars, letters/numbers/_/-, no leading/trailing or doubled punctuation.'); break;
          case 'LENGTH_OUT_OF_RANGE':    setError('Username must be 3–24 characters.'); break;
          case 'EMPTY':                  setError('Username cannot be empty.'); break;
          case 'NOT_AUTHENTICATED':      setError("Your sign-in has expired. Reload and click the magic-link again."); break;
          case 'NO_PROFILE_FOR_AUTH_USER':
            console.error('[EnrolmentModal] set_username returned NO_PROFILE_FOR_AUTH_USER despite upsert');
            setError("Couldn't set username — try again.");
            break;
          default:
            console.error('[EnrolmentModal] set_username returned unknown reason', result);
            setError("Couldn't set username — try again.");
        }
        return;
      }

      // Step 2c — stamp the acceptance fields + flip to enrolled.
      // identity_level lifts to OBSERVER (1) here so the enrolled
      // profile clears the place_pledge RPC gate (>= 1). Without this
      // the column stays at the schema default of 0 and pledges are
      // rejected with "identity_level 0 insufficient for pledging"
      // even though the cockpit header correctly reads "Observer".
      const nowIso = new Date().toISOString();
      const { error: updateErr } = await supabase
        .from('profiles')
        .update({
          age_confirmed_at:       nowIso,
          tnc_accepted_at:        nowIso,
          tnc_version:            TNC_VERSION,
          enrolment_state:        'enrolled',
          identity_level:         IdentityLevel.OBSERVER,
          email_marketing_opt_in: marketingOptIn,
        })
        .eq('id', enrolledProfileId);
      if (updateErr) {
        console.error('[EnrolmentModal] enrolment finalize failed', updateErr);
        setError("Couldn't finalise enrolment. Try again in a moment.");
        return;
      }

      onEnrolled(enrolledProfileId, trimmedEmail, trimmedName);
    } catch (e) {
      console.error('[EnrolmentModal] completeEnrolment threw', e);
      setError("Couldn't complete enrolment. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  }, [authUserId, authEmail, email, username, marketingOptIn, onEnrolled]);

  // Step 1's heading is mode-agnostic — the same magic-link flow
  // handles both first-time enrolment and returning-on-a-new-device
  // sign-in, and the user doesn't know which branch they're on until
  // after the magic-link click-through. Step 2 only ever fires for
  // genuinely new enrolments (resolveSession routes returning users
  // straight to status='enrolled', bypassing Step 2 entirely), so the
  // step-2 heading can stay enrolment-specific.
  const heading = useMemo(() => step === 1 ? 'ENROL / SIGN IN' : 'ENROL · CHOOSE A USERNAME', [step]);

  return (
    <div
      onClick={onClose}
      style={{
        position:        'fixed',
        inset:           0,
        backgroundColor: 'rgba(2,6,23,0.80)',
        backdropFilter:  'blur(3px)',
        zIndex:          1000,
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
        padding:         16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: '#0f172a',
          border:          '1px solid #334155',
          borderTop:       '2px solid #6366f1',
          borderRadius:    10,
          padding:         '22px 24px',
          width:           '100%',
          maxWidth:        420,
          // 2026-05-21 precautionary mobile hardening (brief at
          // docs/audit/2026-05-21-enrolment-modal-hardening-brief.md).
          // Not a reported bug — content normally fits on a phone in
          // portrait. The safety net is for a short viewport
          // (landscape, or a large accessibility font) on Step 2,
          // where paragraph + input + 3 checkboxes + button could in
          // principle exceed the screen. Without these the box would
          // clip with no scroll, trapping the "Complete enrolment"
          // submit below the fold — and Step 2 is the conversion
          // step, so a trapped submit costs a sign-up. With
          // maxHeight bounded and overflow-y: auto on the box, the
          // box scrolls internally if it ever needs to. Dormant when
          // content fits (the normal case). Unlike AboutModal's
          // pre-rewrite scroll issue, the scroll container IS this
          // box (not a flex child), so iOS Safari's flex-resolution
          // quirk on a `max-height + flex:1 child + min-height:0`
          // recipe doesn't apply — overflow-y:auto on a directly
          // bounded box works reliably.
          maxHeight:               '85dvh',
          overflowY:               'auto',
          overscrollBehavior:      'contain',
          WebkitOverflowScrolling: 'touch',
          fontFamily:      'monospace',
          boxShadow:       '0 0 40px rgba(99,102,241,0.2)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <p style={{ color: '#a5b4fc', fontSize: 11, letterSpacing: '0.18em', margin: 0 }}>
            {heading}
          </p>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: '1px solid #334155',
              color: '#64748b',
              fontSize: 11,
              fontFamily: 'monospace',
              borderRadius: 4,
              padding: '2px 7px',
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>

        {step === 1 ? (
          <Step1
            email={email}
            setEmail={setEmail}
            busy={busy}
            error={error}
            onSend={sendMagicLink}
            awaitingLink={awaitingLink}
            otpCode={otpCode}
            setOtpCode={setOtpCode}
            otpBusy={otpBusy}
            otpError={otpError}
            onVerifyOtp={verifyOtpCode}
          />
        ) : (
          <Step2
            username={username}              setUsername={setUsername}
            ageChecked={ageChecked}          setAgeChecked={setAgeChecked}
            tncChecked={tncChecked}          setTncChecked={setTncChecked}
            marketingOptIn={marketingOptIn}  setMarketingOptIn={setMarketingOptIn}
            availability={availability}
            busy={busy}
            error={error}
            onComplete={completeEnrolment}
          />
        )}
      </div>
    </div>
  );
}
